<template>
    <div class="tax-calculator-component">
        <div class="upsert-item__container tax-calculator">
            <section class="upsert-item__section">
                <div class="column-title">Tax & Team Data
                    <div class="tax-calculator-toggle">
                        <p :class="['toggle-label']">Single Salary:</p>
                        <k-switch :on-label="'ON'" :off-label="'OFF'" v-model="singleSalaryView" :size="'small'"></k-switch>
                    </div>
                </div>
                <p class="field--required">*Required</p>
                <div class="input__row teams" v-if="!singleSalaryView">
                    <div class="input__label">
                        *Team
                        <div class="teams-container">
                            <div v-for="(team, index) in incomeData.teams" :key="team.data">
                                <div class="team-row">
                                    <div class="team-row-item input">
                                        <k-dropdown :data-items="taxCalculatorTeamSelects"
                                                    :text-field="'display'"
                                                    :data-item-key="'puckPediaId'"
                                                    v-model="team.data"
                                                    @change="team.hasTeamError = false" />
                                        <p class="team-invalid" v-if="team.hasTeamError">Team required</p>
                                    </div>
                                    <div class="team-row-item input">
                                        <k-numeric-input :placeholder="'Enter salary'" v-model="team.salary" :format="'c0'" :min="0" @keydown="team.hasSalaryError = false"/>
                                        <p class="team-invalid" v-if="team.hasSalaryError">Salary required</p>
                                    </div>
                                    <div class="team-row-item add-subtract">
                                        <k-button class="add-row-btn" title="Add row" v-if="index === incomeData.teams.length - 1" :disabled="!team.salary || !team.data" @click="addTeamInputRow"><i class="fa-solid fa-plus"></i></k-button>
                                        <k-button class="delete-row-btn" title="Delete row" :disabled="incomeData.teams.length <= 1" @click="deleteTeamInputRow(index)"><i class="fa-solid fa-minus"></i></k-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="single-salary-container" v-if="singleSalaryView">
                    <div class="input__row">
                        <div class="input__label">
                            Salary
                            <k-numeric-input :placeholder="'Enter salary'" v-model="singleSalary" :format="'c0'" :min="0" @keydown="singleSalaryError = false"/>
                            <p class="team-invalid" v-if="singleSalaryError">Salary required</p>
                        </div>
                    </div>
                    <div class="input__row teams-label">
                        <div class="input__label">Team(s)</div>
                    </div>
                    <div class="team-checkboxes" :key="checkboxKey">
                        <div class="checkbox-container select-all">
                            <k-checkbox v-model="allTeamsSelected" :label="'Select All'" @change="onAllTeamsSelectedChange($event)" />
                        </div>
                        <div class="checkbox-container">                            
                            <p class="single-salary team-invalid" v-if="singleTeamsError">Team(s) required</p>
                        </div>
                        <div class="checkbox-container" v-for="(team, index) in taxCalculatorTeamSelects" :key="index">
                            <k-checkbox v-model="team.selected" :label="team.display" @change="onTeamSelectChange($event, team)" />
                        </div>

                    </div>
                </div>
                <div class="calculate-btns">
                    <k-button class="calculate-btn" @click="onSubmit">Submit</k-button>
                    <k-button class="cancel-btn" @click="onClear">Clear</k-button>
                </div>
            </section>
        </div>
        <div class="results-list-container">
            <div class="column-title">Income Tax Results
                <k-button class="pdf-btn" :disabled="resultRows?.length === 0" @click="togglePlayerNameDialog"><i class="fa-solid fa-file-pdf"></i> Generate PDF</k-button>
            </div>
            <k-listview class="results-list" v-if="resultRows.length > 0"
                        :data-items="resultRows"
                        :item="'result'"
                        :header="'resultsHeader'"
                        :footer="'resultsFooter'">
                <template v-slot:result="{ props }">
                    <div class="result-container">
                        <div class="result-row">
                            <div class="result-row-item team-name">
                                <img :src="props.dataItem.teamLogo" />
                                <p>{{ props.dataItem.team }}</p>
                            </div>
                            <div class="result-row-item amount">
                                <p>{{ $options.filters.formatCurrency(props.dataItem.income) }}</p>
                            </div>
                            <div class="result-row-item rate">
                                <p>{{ (props.dataItem.federal_effective_rate * 100).toFixed(2) }} %</p>
                            </div>
                            <div class="result-row-item amount">
                                <p>{{ $options.filters.formatCurrency(props.dataItem.federal_taxes_owed) }}</p>
                            </div>
                            <div class="result-row-item rate">
                                <p>{{ (props.dataItem.region_effective_rate * 100).toFixed(2) }} %</p>
                            </div>
                            <div class="result-row-item rate">
                                <p>{{ (props.dataItem.local_effective_rate * 100).toFixed(2) }} %</p>
                            </div>
                            <div class="result-row-item rate">
                                <p>{{ (props.dataItem.total_effective_rate * 100).toFixed(2) }} %</p>
                            </div>
                            <div class="result-row-item amount">
                                <p>{{ $options.filters.formatCurrency(props.dataItem.region_taxes_owed) }}</p>
                            </div>
                            <div class="result-row-item amount">
                                <p>{{ $options.filters.formatCurrency(props.dataItem.total_taxes_owed) }}</p>
                            </div>
                            <div class="result-row-item amount post-tax">
                                <p>{{ $options.filters.formatCurrency(props.dataItem.income_after_tax) }}</p>
                            </div>
                        </div>
                    </div>
                </template>
                <template v-slot:resultsHeader>
                    <k-listview-header>
                        <div class="result-container header">
                            <div class="result-row">
                                <div class="result-row-item team-name">Team</div>
                                <div class="result-row-item amount">Contract</div>
                                <div class="result-row-item rate">Fed. Rate</div>
                                <div class="result-row-item amount">Fed. Tax</div>
                                <div class="result-row-item rate">State Rate</div>
                                <div class="result-row-item rate">Local Rate</div>
                                <div class="result-row-item rate">Total Rate</div>
                                <div class="result-row-item amount">Local Tax</div>
                                <div class="result-row-item amount">Total Tax</div>
                                <div class="result-row-item amount">Net Contract</div>
                            </div>
                        </div>
                    </k-listview-header>
                </template>
                <template v-slot:resultsFooter>
                    <!-- <k-listview-footer></k-listview-footer> -->
                </template>
            </k-listview>

            <div class="no-results-alert">
                <i class="fa-sharp-duotone fa-solid fa-chart-simple"></i>
                <p class="no-results-text">No results available</p>
                <p class="no-results-subtext">Submit calculator data to compare results</p>
            </div>
        </div>

        <div class="loader-container" v-if="isLoading">
            <k-loader :size="'large'" :type="'infinite-spinner'"></k-loader>
        </div>

        <transition name="dialogfade">
            <k-dialog v-if="showPlayerNameDialog" :title="'Analysis Recipient'" class="default__dialog player-name-dialog" @close="togglePlayerNameDialog">
                <label>
                    Player Name (optional)
                    <k-input v-model="playerName"></k-input>
                </label>
                <k-action-bar>
                    <k-button class="item-save__btn" @click="onGenerateAnalysis">Save</k-button>
                    <k-button class="item-cancel__btn" @click="togglePlayerNameDialog">Cancel</k-button>
                </k-action-bar>
            </k-dialog>
        </transition>

        <transition name="dialogfade">
            <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
                <p class="error-dialog__msg">{{ errorMsg }}</p>
            </k-dialog>
        </transition>
    </div>
</template>

<script>
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs';
    import { Button } from "@progress/kendo-vue-buttons";
    import { NumericTextBox, Checkbox, Input } from "@progress/kendo-vue-inputs";
    import { DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Switch } from "@progress/kendo-vue-inputs";

    import {
    ListView,
    ListViewHeader,
    } from '@progress/kendo-vue-listview';

    import cloneDeep from 'lodash.clonedeep';

    import playerHelpers from '@/helpers/playerHelpers';
    import taxCalculatorHelpers from '@/helpers/taxCalculatorHelpers';

    import auth from '../authConfig';
    import apiService from '../api/api.service.js';

    export default {
        name: 'taxCalculatorComponent',
        props: ['teamLogos'],
        data() {
            return {
                allTeamsSelected: false,
                checkboxKey: 0,
                defaultTeamRow: { data: { display: 'Select team', puckPediaId: null }, salary: null, hasTeamError: false, hasSalaryError: false },
                errorMsg: '',
                errorTitle: '',
                incomeData: {
                    teams: [{ data: { display: 'Select team', puckPediaId: null }, salary: null, hasTeamError: false, hasSalaryError: false }]
                },
                incomeDataModel: {
                    teams: [{ data: { display: 'Select team', puckPediaId: null }, salary: null, hasTeamError: false, hasSalaryError: false }]
                },
                isLoading: false,
                playerName: null,
                resultRows: [],
                showErrorDialog: false,
                showPlayerNameDialog: false,
                singleSalary: null,
                singleSalaryError: false,
                singleSalaryView: false,
                singleTeamsError: false,
                teamTaxRates: []
            }
        },
        computed: {
            nhlTeamSelects() {
                return playerHelpers.nhlTeamSelects;
            },
            taxCalculatorTeamSelects() {
                return cloneDeep(taxCalculatorHelpers.taxCalculatorTeamSelects);
            }
        },
        watch: {
        },
        mounted() {
            this.getTeamTaxRates();
        },
        methods: {
            addTeamInputRow() {
                this.incomeData.teams.push(cloneDeep(this.defaultTeamRow));
            },
            deleteTeamInputRow(rowIndex) {
                let teams = cloneDeep(this.incomeData.teams);
                teams.splice(rowIndex, 1);
                console.log(teams)

                this.incomeData.teams = teams;
            },
            async generateAnalysis(request) {
                let self = this;
                self.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GenerateContractAnalysisPDF(tokenResponse.accessToken, request)
                    .then(res => {
                        self.openPDF(res.data.data);
                        self.isLoading = false;
                    }).catch(error => {
                        console.log(error);
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            async getTeamTaxRates() {
                let self = this;
                self.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.GetTeamTaxRates(tokenResponse.accessToken)
                    .then(res => {
                        if (res.data.data) {
                            self.teamTaxRates = res.data.data;
                            self.isLoading = false;
                        }
                    }).catch(error => {
                        self.onRequestError('An Error Occurred', error?.response?.data?.message);
                    });
            },
            onAllTeamsSelectedChange(evt) {
                this.taxCalculatorTeamSelects.forEach(team => team.selected = evt.value);
                this.checkboxKey += 1;
            },
            onClear() {
                this.incomeData = cloneDeep(this.incomeDataModel);
                this.singleSalaryError = false;
                this.singleTeamsError = false;
                this.singleSalary = null;
                this.taxCalculatorTeamSelects.forEach(team => team.selected = false);
                this.allTeamsSelected = false;
            },
            onGenerateAnalysis() {
                let playerName = this.playerName;

                this.togglePlayerNameDialog();

                let request = {
                    analysisDate: new Date().toLocaleDateString(),
                    playerName: playerName,
                    taxItems: this.resultRows.map(row => {
                        return {
                            teamName: row.team,
                            contract: this.$options.filters.formatCurrency(row.income),
                            fedRate: `${(row.federal_effective_rate * 100).toFixed(2)}%`,
                            fedTax: this.$options.filters.formatCurrency(row.federal_taxes_owed),
                            localRate: `${((row.region_effective_rate + row.local_effective_rate) * 100).toFixed(2)}%`,
                            localTax: this.$options.filters.formatCurrency(row.region_taxes_owed),
                            totalTax: this.$options.filters.formatCurrency(row.total_taxes_owed),
                            netContract: this.$options.filters.formatCurrency(row.income_after_tax)
                        }
                    })
                }

                this.generateAnalysis(request);
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                this.showErrorDialog = true;
            },
            async onSubmit() {
                let resultRows = [];

                if (!this.singleSalaryView) {
                    if (this.incomeData.teams.some(team => !team.data.puckPediaId || !team.salary))
                    {
                        return this.incomeData.teams.forEach(team => {
                            if (!team.salary) {
                                team.hasSalaryError = true;
                            }

                            if (!team.data.puckPediaId) {
                                team.hasTeamError = true;
                            }
                        });
                    }

                    this.incomeData.teams.forEach(team => {
                        let teamTaxRates = this.teamTaxRates.find(rates => rates.puckPediaId === team.data.puckPediaId);

                        if (teamTaxRates) {
                            let fedRate = teamTaxRates.fedTaxRate ? teamTaxRates.fedTaxRate / 100 : 0;
                            let stateRate = teamTaxRates.stateTaxRate ? teamTaxRates.stateTaxRate / 100 : 0;
                            let localRate = teamTaxRates.localTaxRate ? teamTaxRates.localTaxRate / 100 : 0;

                            let totalRate = [fedRate, stateRate, localRate].reduce((acc, cur) => {
                                return acc + cur;
                            }, 0);

                            resultRows.push({
                                team: team.data.display,
                                income: team.salary,
                                puckPediaId: team.data.puckPediaId,
                                federal_effective_rate: fedRate,
                                federal_taxes_owed: team.salary * fedRate,
                                region_effective_rate: stateRate,
                                local_effective_rate: localRate,
                                total_effective_rate:   totalRate,
                                region_taxes_owed: team.salary * (stateRate + localRate),
                                total_taxes_owed: team.salary * totalRate,
                                income_after_tax: team.salary - (team.salary * totalRate),
                                teamLogo: this.teamLogos.find(logo => logo.teamId === team.data.puckPediaId)?.logo
                            });
                        }
                    });
                } else {
                    if (!this.singleSalary || !this.taxCalculatorTeamSelects.some(team => team.selected)) {
                        if (!this.singleSalary) {
                            this.singleSalaryError = true;
                        }

                        if (!this.taxCalculatorTeamSelects.some(team => team.selected)) {
                            this.singleTeamsError = true;
                        }
                    } else {
                        let selectedTeams = this.taxCalculatorTeamSelects.filter(team => team.selected);

                        selectedTeams.forEach(team => {
                            let teamTaxRates = this.teamTaxRates.find(rates => rates.puckPediaId === team.puckPediaId);

                            if (teamTaxRates) {
                                let fedRate = teamTaxRates.fedTaxRate ? teamTaxRates.fedTaxRate / 100 : 0;
                                let stateRate = teamTaxRates.stateTaxRate ? teamTaxRates.stateTaxRate / 100 : 0;
                                let localRate = teamTaxRates.localTaxRate ? teamTaxRates.localTaxRate / 100 : 0;

                                let totalRate = [fedRate, stateRate, localRate].reduce((acc, cur) => {
                                    return acc + cur;
                                }, 0);

                                resultRows.push({
                                    team: team.display,
                                    income: this.singleSalary,
                                    puckPediaId: team.puckPediaId,
                                    federal_effective_rate: fedRate,
                                    federal_taxes_owed: this.singleSalary * fedRate,
                                    region_effective_rate: stateRate,
                                    local_effective_rate: localRate,
                                    total_effective_rate:   totalRate,
                                    region_taxes_owed: this.singleSalary * (stateRate + localRate),
                                    total_taxes_owed: this.singleSalary * totalRate,
                                    income_after_tax: this.singleSalary - (this.singleSalary * totalRate),
                                    teamLogo: this.teamLogos.find(logo => logo.teamId === team.puckPediaId)?.logo
                                })
                            }
                        });
                    }
                }

                this.resultRows = resultRows.sort((a, b) => b.income_after_tax - a.income_after_tax);
            },
            onTeamSelectChange(evt, team) {
                this.checkboxKey += 1;
                
                if (this.taxCalculatorTeamSelects.some(team => !team.selected)) {
                    this.allTeamsSelected = false;
                } else {
                    this.allTeamsSelected = true;
                }
            },
            openPDF(src) {
                if (!src) return;
                
                let pdfWindow = window.open("");

                setTimeout(() => {
                    if (pdfWindow.document) {
                        pdfWindow.document.write("<iframe width='100%' height='100%' src='data:application/pdf;base64, " + encodeURI(src) + "'></iframe>");
                    }
                }, 2000)
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorTitle = '';
                    this.errorMsg = '';
                }
            },
            togglePlayerNameDialog() {
                this.showPlayerNameDialog = !this.showPlayerNameDialog;

                if (!this.showPlayerNameDialog) {
                    this.playerName = null;
                }
            }
        },
        filters: {
            formatCurrency(val) {
                if (val === null || val === undefined || val === '') return '';

                if (val.toString().includes('$')) return val;

                if (isNaN(val)) {
                    val = 0;
                }

                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                });

                return formatter.format(val);
            }
        },
        components: {
            'k-loader': Loader,
            'k-dialog': Dialog,
            'k-listview': ListView,
            'k-listview-header': ListViewHeader,
            'k-button': Button,
            'k-numeric-input': NumericTextBox,
            'k-dropdown': DropDownList,
            'k-switch': Switch,
            'k-checkbox': Checkbox,
            'k-input': Input,
            'k-action-bar': DialogActionsBar
        }
    }
</script>

<style>
    @import '/src/assets/css/taxCalculatorComponent.css';
</style>